import React from "react";

import { InboxOutlined } from "@ant-design/icons";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { Button, Alert, renderMarkdown } from "@impero-com/ui";
import { Upload } from "antd";
import { UploadFile } from "antd/es/upload/interface";

import { ProcessFileResult } from "../hooks/useSpreadsheetProcessor";
import { BulkImportStep } from "../stepsContainer";

import * as theme from "./theme.css";
import { ValidationErrors } from "./validationErrors";

const { Dragger } = Upload;

interface UploadStepProps {
    onStepChange: (step: BulkImportStep) => void;
    onErrorChange: (hasError: boolean) => void;
    processFile: (file: File) => Promise<ProcessFileResult>;
    resetData: () => void;
    validationErrors: string[];
}

export function UploadStep({
    onStepChange,
    onErrorChange,
    processFile,
    resetData,
    validationErrors,
}: UploadStepProps) {
    const { t } = useTranslation();
    const [file, setFile] = React.useState<UploadFile | null>(null);

    const handleFileSelect = async (file: File) => {
        onErrorChange(false);

        try {
            const uploadFile: UploadFile = {
                ...file,
                uid: Date.now().toString(),
            };
            setFile(uploadFile);

            const result = await processFile(file);

            if (result.success) {
                onStepChange("confirm");
            } else {
                onErrorChange(true);
            }
        } catch (error) {
            console.error("File processing error:", error);
            setFile(null);
            resetData();
            onErrorChange(true);
        }
    };

    const handleBack = () => {
        onStepChange("export");
    };

    const handleFileRemove = () => {
        setFile(null);
        resetData();
    };

    return (
        <div className={theme.stepContainer}>
            <h2>{t("ControlsBulkImport_UploadStep_Title")}</h2>
            <Alert
                showIcon
                type="info"
                message={t("ControlsBulkImport_UploadStep_Instructions")}
                className={theme.infoAlertBox}
            />

            <Dragger
                accept=".xlsx"
                beforeUpload={(file) => {
                    handleFileSelect(file);
                    return false;
                }}
                fileList={file ? [file] : []}
                onRemove={handleFileRemove}
            >
                <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                    {t(
                        "ControlsBulkImport_UploadStep_UploadContainerInstructions"
                    )}
                </p>
            </Dragger>

            {validationErrors.length > 0 && (
                <>
                    <ValidationErrors errors={validationErrors} />
                    <Alert
                        showIcon
                        type="warning"
                        message={renderMarkdown(
                            t("ControlsBulkImport_ExportStep_Instructions")
                        )}
                        className={theme.errorInstructionsBox}
                    />
                </>
            )}

            <div className={theme.buttonGroup}>
                <Button onClick={handleBack}>{t("Back")}</Button>
            </div>
        </div>
    );
}
